import { useField, useFormikContext } from 'formik'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useBrandVoices } from 'hooks/useBrandVoices'
import { DesignWizardField } from '../DesignWizardField'
import { noop } from 'lodash'
import { BrandVoicePreview } from 'screens/BrandKits/components/BrandVoice/BrandVoiceCard/BrandVoicePreview'
import { PressableBrandAICard } from 'screens/BrandKits/components/BrandAICard/BrandAICard'
import { useModals } from 'almanac-ui/Modal'
import { BrandVoicesModal } from './BrandVoicesModal'
import { Tone } from 'components/VoiceAndTonesSelect/Types'
import { TonesPicker } from './TonesPicker'
import { Loading } from '../Loading'
import { Plus } from 'blaze-ui/icons'
import { useOpenBrandVoiceModal } from 'screens/BrandKits/components/BrandVoice/useOpenBrandVoiceModal'
import { AnalysingContentModal } from 'screens/BrandKits/components/BrandVoice/AddVoice/AnalysingContentModal'
import { EditVoiceModal } from 'screens/BrandKits/components/BrandVoice/EditVoiceModal'

export const BrandVoiceField = () => {
  const { openModal, closeModals, closeStack } = useModals()
  const currentWorkspace = useCurrentWorkspace()
  const { data: voices, isLoading } = useBrandVoices(currentWorkspace)
  const openBrandVoiceModal = useOpenBrandVoiceModal()
  const [brandVoiceIdField, ,] = useField('brand_voice_id')
  const [toneField, , { setValue: setTone }] = useField('tone')
  const { setValues } = useFormikContext<{ [key: string]: any }>()

  const setBrandVoiceId = (voiceId: number | null) => {
    setValues((values) => ({
      ...values,
      brand_voice_id: voiceId,
    }))
  }

  const anyVoices = !!voices?.length
  const selectedVoice = voices?.find((voice) => voice.id === brandVoiceIdField.value)
  const voiceLabel = anyVoices ? 'Select a Brand Voice' : 'Create a Brand Voice'

  const openVoicesModal = () => {
    if (!voices) return

    openModal(
      BrandVoicesModal,
      {
        initialVoice: selectedVoice,
        voices: voices,
        initialTones: toneField.value,
        onVoiceSelect: (voiceId) => {
          setBrandVoiceId(voiceId === selectedVoice?.id ? null : voiceId)
          setTone([])
        },
        onTonesSelect: (tones: Tone[]) => {
          setBrandVoiceId(null)
          setTone(tones)
        },
      },
      { newStack: true },
    )
  }

  const openAddVoiceModal = () => {
    openBrandVoiceModal(
      {
        onAnalyse: (analyseProps) =>
          openModal(AnalysingContentModal, {
            ...analyseProps,
            onNext: (editProps) =>
              openModal(EditVoiceModal, {
                ...editProps,
                onBack: () => {
                  closeModals(2)
                },
                onCreate: ({ voiceId }) => {
                  closeStack()
                  setBrandVoiceId(voiceId)
                },
              }),
          }),
      },
      { newStack: true },
    )
  }

  if (isLoading) return <Loading />

  return (
    <DesignWizardField
      title="Brand Voice"
      onButtonPress={anyVoices ? openVoicesModal : openAddVoiceModal}
      buttonLabel={selectedVoice ? 'Change Brand Voice' : voiceLabel}
      withChecklist={!selectedVoice}
      buttonIcon={!selectedVoice && !anyVoices ? Plus : undefined}
    >
      {selectedVoice && (
        <PressableBrandAICard
          title={selectedVoice.voice_params.name}
          lineClamp={1}
          onPress={openVoicesModal}
          preview={() => (
            <BrandVoicePreview
              voice={selectedVoice}
              onEmojiSelect={noop}
              onRemoveEmoji={noop}
              isEmojiMenuOpen={false}
              onEmojiMenuOpenChange={noop}
            />
          )}
        />
      )}

      {!selectedVoice && <TonesPicker setTone={setTone} selectedTones={toneField.value || []} />}
    </DesignWizardField>
  )
}
